import { ReactNode, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '../hooks/useResponsive';
import { useOrientation } from '../hooks/useOrientation';
import { colors } from '../core/theme/theme';

interface Props {
  children: ReactNode;
}

/**
 * Wraps a view that reads best in landscape on mobile (wide tables like the
 * timesheet grid or the calendar). On mobile + portrait, renders a blocking
 * overlay that asks the user to rotate. Once the user either rotates or taps
 * "Continue anyway", the children render normally.
 *
 * The dismissal is per-mount: if the user rotates back to portrait after
 * dismissing, the overlay stays hidden. It only resets when the view is
 * remounted.
 */
export default function OrientationPrompt({ children }: Props) {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const orientation = useOrientation();
  const [dismissed, setDismissed] = useState(false);

  // Rotating back to landscape clears any latent "dismissed in portrait" state
  // and also hides the overlay naturally. We reset `dismissed` so a subsequent
  // rotate-to-portrait shows the prompt again (users who explicitly rotate
  // likely want the reminder if they go back).
  useEffect(() => {
    if (orientation === 'landscape') setDismissed(false);
  }, [orientation]);

  const showOverlay = isMobile && orientation === 'portrait' && !dismissed;

  return (
    <>
      {children}
      {showOverlay && (
        <Box
          role="dialog"
          aria-modal="true"
          aria-label={t('orientation.rotatePrompt')}
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: colors.darkBg,
            color: '#FFFFFF',
            zIndex: 1400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            px: 3,
            textAlign: 'center',
          }}
        >
          <ScreenRotationIcon
            sx={{
              fontSize: 72,
              color: colors.yellow,
              animation: 'orientationRotate 1.8s ease-in-out infinite',
              '@keyframes orientationRotate': {
                '0%, 100%': { transform: 'rotate(0deg)' },
                '50%': { transform: 'rotate(90deg)' },
              },
            }}
          />
          <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
            {t('orientation.rotatePrompt')}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setDismissed(true)}
            sx={{
              borderColor: 'rgba(255,255,255,0.4)',
              color: '#FFFFFF',
              '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            {t('orientation.continueAnyway')}
          </Button>
        </Box>
      )}
    </>
  );
}
